import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Lock,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  User,
  Eye,
  EyeOff
} from 'lucide-react';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    plan: 'Gold'
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  const selectedMode = searchParams.get('mode');
  const selectedPlanFromUrl = searchParams.get('plan');
  const availablePlans = useMemo(() => ['Standard', 'Gold', 'Platinum'], []);

  useEffect(() => {
    if (selectedMode === 'register') {
      setIsLogin(false);
    }

    if (selectedPlanFromUrl && availablePlans.includes(selectedPlanFromUrl)) {
      setFormData((current) => ({ ...current, plan: selectedPlanFromUrl }));
    } else {
      setFormData((current) => ({ ...current, plan: current.plan || 'Gold' }));
    }
  }, [availablePlans, selectedMode, selectedPlanFromUrl]);

  const validateForm = () => {
    const { name, email, password, phone } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-z\s]+$/;
    const phoneRegex = /^\d{10}$/;

    if (!email) {
      return 'Email is required';
    }

    if (!emailRegex.test(email)) {
      return 'Enter a valid email address';
    }

    if (isLogin) {
      if (!password) {
        return 'Email and Password are required';
      }

      if (password.length < 6 || password.length > 20) {
        return 'Password must be between 6 and 20 characters';
      }
    } else {
      if (!name || !phone) {
        return 'All fields are required';
      }

      if (!nameRegex.test(name)) {
        return 'Name should contain only letters';
      }

      if (!phoneRegex.test(phone)) {
        return 'Phone number must be exactly 10 digits';
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        const whatsappMessage = [
          `I am ${formData.name}.`,
          'My details are:',
          `Email: ${formData.email}`,
          `Phone: ${formData.phone}`,
          `I am interested in the ${formData.plan} plan.`
        ].join('\n');

        const whatsappUrl = `https://wa.me/919067611076?text=${encodeURIComponent(whatsappMessage)}`;
        window.location.href = whatsappUrl;
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const authHighlights = [
    'Modern PG operations',
    'Monthwise business visibility',
    'Responsive across devices'
  ];

  const formCardBase =
    'group rounded-2xl border border-slate-200 bg-white px-4 py-3 transition-all focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100';

  return (
    <div className="min-h-screen bg-[#f6f7f2] text-slate-900">
      <div className="absolute inset-x-0 top-0 h-[520px] overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-dot-grid opacity-60" />
        <div className="absolute left-[-10%] top-[-4%] h-64 w-64 rounded-full bg-emerald-200/45 blur-3xl animate-pulse-soft" />
        <div className="absolute right-[2%] top-[8%] h-56 w-56 rounded-full bg-cyan-200/35 blur-3xl animate-pulse-soft animation-delay-300" />
      </div>

      <div className="relative min-h-screen px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-white"
            data-testid="back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>

          <div className="hidden sm:flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 shadow-[0_12px_30px_rgba(15,23,42,0.16)]">
              <Sparkles className="h-5 w-5 text-emerald-300" />
            </div>
            <div className="text-left">
              <p className="text-lg font-bold tracking-tight text-slate-950">Solucate</p>
              <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">PG Operations OS</p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 grid max-w-7xl gap-8 lg:mt-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="order-2 lg:order-1 animate-fade-up">
            <div className="max-w-xl">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse-soft" />
                Secure access to the Solucate workspace
              </div>

              <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl">
                {isLogin ? 'Welcome back to Solucate.' : 'Create your Solucate account.'}
              </h1>

              <p className="mt-5 text-lg leading-8 text-slate-600">
                {isLogin
                  ? 'Sign in to manage tenants, occupancy, collections, and monthly financial visibility from one polished dashboard.'
                  : 'Share your interest on WhatsApp and we will help you get started with the right Solucate plan.'}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {authHighlights.map((item, index) => (
                  <div
                    key={item}
                    className={`rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 text-sm font-medium text-slate-700 shadow-sm ${index === 1 ? 'animate-fade-up animation-delay-150' : index === 2 ? 'animate-fade-up animation-delay-300' : 'animate-fade-up'}`}
                  >
                    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950">
                    <ShieldCheck className="h-5 w-5 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">Trusted access</p>
                    <p className="text-lg font-semibold text-slate-950">One login for operational clarity</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  The auth experience is designed to feel lighter and more professional on every screen size, while preserving the same login and registration flow underneath.
                </p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 animate-fade-up animation-delay-150">
            <div className="glass-panel overflow-hidden rounded-[30px] border border-white/70 p-3 shadow-[0_30px_80px_rgba(15,23,42,0.12)] sm:p-4">
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
                      {isLogin ? 'Login' : 'Register'}
                    </p>
                    <h2 className="mt-1 text-2xl font-bold text-slate-950">
                      {isLogin ? 'Access your workspace' : 'Set up your operator account'}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {isLogin
                        ? 'Continue where you left off with your PG operations.'
                        : 'Send your details on WhatsApp to request access and confirm the right plan.'}
                    </p>
                  </div>
                  <div className="flex w-full rounded-2xl bg-slate-100 p-1 sm:w-auto">
                    <button
                      type="button"
                      className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition-all ${isLogin ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                      onClick={() => setIsLogin(true)}
                      data-testid="switch-login"
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition-all ${!isLogin ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                      onClick={() => setIsLogin(false)}
                      data-testid="switch-register"
                    >
                      Register
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {!isLogin && (
                    <div>
                      <Label className="mb-2 block text-sm font-semibold text-slate-700">Full Name</Label>
                      <div className={formCardBase}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                            <User className="h-4 w-4" />
                          </div>
                          <Input
                            type="text"
                            placeholder="Enter your full name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                            data-testid="auth-name-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Email</Label>
                    <div className={formCardBase}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                          <Mail className="h-4 w-4" />
                        </div>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                          data-testid="auth-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {!isLogin && (
                    <div>
                      <Label className="mb-2 block text-sm font-semibold text-slate-700">Phone Number</Label>
                      <div className={formCardBase}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                            <Phone className="h-4 w-4" />
                          </div>
                          <Input
                            type="tel"
                            placeholder="Enter your phone number"
                            value={formData.phone}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d{0,10}$/.test(value)) {
                                setFormData({ ...formData, phone: value });
                              }
                            }}
                            className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                            data-testid="auth-phone-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {!isLogin && (
                    <div>
                      <Label className="mb-2 block text-sm font-semibold text-slate-700">Interested Plan</Label>
                      <div className={formCardBase}>
                        <select
                          value={formData.plan}
                          onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                          className="w-full border-0 bg-transparent px-0 text-sm font-medium text-slate-900 shadow-none outline-none"
                          data-testid="auth-plan-select"
                        >
                          {availablePlans.map((plan) => (
                            <option key={plan} value={plan}>
                              {plan}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* {isLogin && (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-4">
                        <Label className="block text-sm font-semibold text-slate-700">Password</Label>
                        <span className="text-xs text-slate-500">6-20 characters</span>
                      </div>
                      <div className={formCardBase}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                            <Lock className="h-4 w-4" />
                          </div>
                          <Input
                            type="password"
                            placeholder="Enter your password"
                            value={formData.password}
                            maxLength={20}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                            data-testid="auth-password-input"
                          />
                        </div>
                      </div>
                    </div>
                  )} */}
                  {isLogin && (
  <div>
    <div className="mb-2 flex items-center justify-between gap-4">
      <Label className="block text-sm font-semibold text-slate-700">
        Password
      </Label>
      <span className="text-xs text-slate-500">6-20 characters</span>
    </div>

    {/* IMPORTANT: add 'group' here */}
    <div className={`group ${formCardBase}`}>
      <div className="flex items-center gap-3 w-full">

        {/* 🔒 Lock Icon */}
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
          <Lock className="h-4 w-4" />
        </div>

        {/* 🔑 Password Input */}
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="Enter your password"
          value={formData.password}
          maxLength={20}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 flex-1"
          data-testid="auth-password-input"
        />

        {/* 👁 Eye Toggle Icon */}
        <div
          onClick={() => setShowPassword(!showPassword)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 cursor-pointer group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600"
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </div>

      </div>
    </div>
  </div>
)}

                  <Button
                    type="submit"
                    className="mt-2 h-14 w-full rounded-2xl bg-slate-950 text-base font-semibold text-white hover:bg-slate-800"
                    disabled={loading}
                    data-testid="auth-submit-button"
                  >
                    {loading ? 'Please wait...' : isLogin ? 'Login to Solucate' : 'Continue to WhatsApp'}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm text-slate-500">
                  {isLogin ? 'New to Solucate?' : 'Already have an account?'}{' '}
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="font-semibold text-emerald-700 hover:text-emerald-800"
                    data-testid="auth-toggle-link"
                  >
                    {isLogin ? 'Create your account' : 'Sign in here'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
